import { Component } from 'preact';
import { createPortal } from 'preact/compat';
import {
  buildSorceryDeckMetrics,
  getCardElementShares,
  ELEMENT_ORDER,
  SORCERY_CHART_COLORS,
  KEYWORD_DEFS,
} from '../utils/sorcery/deckMetrics';
import { ChartNoiseDefs, noiseBarStyle } from './deckStats/ChartNoise';
import { ManaCurveChart } from './deckStats/CurvesPanel';
import ElementIcon from './deckStats/ElementIcon';
import {
  CONTENT_BG_DEFAULT,
  PANEL_BG,
  GOLD,
  ACCENT_GOLD,
  TEXT_PRIMARY,
  TEXT_BODY,
  TEXT_MUTED,
  FourCorners,
} from '../lib/medievalTheme';
import { t } from '../localization/t';

const KEYWORD_LABEL = Object.fromEntries(KEYWORD_DEFS.map((d) => [d.key, d.label]));

/**
 * Element pip used across the footer's legends. Delegates to the
 * shared <ElementIcon> for the four real elements (same SVG tokens the
 * collection screen uses) and renders a small gold orb for `Neutral`
 * since it has no in-game element token.
 */
function ElementPip({ element, size = 12, muted = false }) {
  if (element === 'Neutral') {
    return (
      <span
        aria-label={t('Neutral')}
        className="shrink-0 inline-block rounded-full"
        style={{
          width: `${size / 16}cqw`,
          height: `${size / 16}cqw`,
          background: `radial-gradient(circle at 30% 30%, ${ACCENT_GOLD} 0%, ${SORCERY_CHART_COLORS.Neutral} 65%, rgba(60,46,22,0.9) 100%)`,
          boxShadow: muted
            ? 'inset 0 0 0 1px rgba(255,255,255,0.06)'
            : 'inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(0,0,0,0.5)',
          opacity: muted ? 0.4 : 1,
          filter: muted ? 'grayscale(1)' : 'none',
        }}
      />
    );
  }
  return <ElementIcon element={element} size={size} muted={muted} />;
}

/**
 * Element-aware mana stats. Average printed cost per element, weighted
 * by each card's element shares (so a dual-element card splits its cost
 * evenly across both bars instead of biasing a single element). Computed
 * over spellbook entries only; sites have no cost in the same sense.
 */
function avgCostByElement(entries) {
  const totals = {};
  const counts = {};
  for (const el of [...ELEMENT_ORDER, 'Neutral']) {
    totals[el] = 0;
    counts[el] = 0;
  }
  for (const entry of entries) {
    if (entry?.zone !== 'spellbook') continue;
    const card = entry.card;
    const cost = Number(card?.cost);
    if (!Number.isFinite(cost)) continue;
    for (const [el, share] of getCardElementShares(card)) {
      totals[el] += cost * share;
      counts[el] += share;
    }
  }
  const result = {};
  for (const el of [...ELEMENT_ORDER, 'Neutral']) {
    result[el] = counts[el] === 0 ? 0 : totals[el] / counts[el];
  }
  return result;
}

/**
 * Horizontal stacked bar of element counts (Water/Earth/Fire/Air/Neutral)
 * across the supplied entries. Sums each card's *element shares* so a
 * dual-element card contributes half a tick to each of its elements
 * rather than being slotted under whichever was listed first.
 */
function ElementBar({ entries, dimEmpty = true }) {
  const counts = {};
  for (const el of [...ELEMENT_ORDER, 'Neutral']) counts[el] = 0;
  for (const entry of entries) {
    if (!entry?.card) continue;
    if (entry.zone === 'atlas' || entry.zone === 'avatar') continue;
    for (const [el, share] of getCardElementShares(entry.card)) {
      counts[el] += share;
    }
  }
  const order = [...ELEMENT_ORDER, 'Neutral'];
  const total = order.reduce((s, el) => s + counts[el], 0);

  if (total === 0) {
    return (
      <div className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>
        {dimEmpty ? t('No cards yet.') : ''}
      </div>
    );
  }

  return (
    <div>
      <div
        className="flex w-full h-[0.875cqw] rounded-sm overflow-hidden"
        style={{
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.05), inset 0 -1px 0 rgba(0,0,0,0.25), 0 1px 0 rgba(0,0,0,0.5)',
          border: `1px solid ${GOLD} 0.25)`,
        }}
      >
        {order.map((el) => {
          const value = counts[el];
          if (value <= 0) return null;
          const pct = (value / total) * 100;
          const color = SORCERY_CHART_COLORS[el] || SORCERY_CHART_COLORS.Neutral;
          return (
            <div
              key={el}
              style={{
                width: `${pct}%`,
                ...noiseBarStyle(`linear-gradient(180deg, ${color} 0%, ${color}cc 100%)`),
              }}
              title={t('{element}: {count}', { element: t(el), count: value.toFixed(value === Math.floor(value) ? 0 : 1) })}
            />
          );
        })}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[0.6875cqw] tabular-nums">
        {order.map((el) => {
          const value = counts[el];
          const display = Number.isInteger(value) ? value : value.toFixed(1);
          const empty = value <= 0;
          return (
            <div key={el} className="flex items-center gap-1" style={{ opacity: empty ? 0.4 : 1 }}>
              <ElementPip element={el} muted={empty} />
              <span style={{ color: TEXT_PRIMARY }}>{display}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Horizontal bars showing average mana cost per element. One row per
 * element that actually has data; bar length is proportional to a fixed
 * cap (5 cost) so the visual stays comparable across the two footer
 * panels even when both are sparse.
 */
function AvgCostByElementBars({ entries }) {
  const avgs = avgCostByElement(entries);
  const order = [...ELEMENT_ORDER, 'Neutral'];
  const populated = order.filter((el) => avgs[el] > 0);
  if (populated.length === 0) {
    return (
      <div className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>
        {t('No costs yet.')}
      </div>
    );
  }
  // Cap at 5 so the bar lengths read as "is this element cheap or
  // expensive on average?" rather than auto-scaling to the dataset
  // max (which would make a Neutral 1.0 look gigantic next to a 1.2).
  const MAX = 5;
  return (
    <div className="flex flex-col gap-[0.4cqw]">
      {populated.map((el) => {
        const avg = avgs[el];
        const pct = Math.min(100, (avg / MAX) * 100);
        const color = SORCERY_CHART_COLORS[el] || SORCERY_CHART_COLORS.Neutral;
        return (
          <div key={el} className="flex items-center gap-2">
            <span
              className="shrink-0 flex items-center justify-center"
              style={{ width: '1.25cqw' }}
            >
              <ElementPip element={el} size={14} />
            </span>
            <div
              className="flex-1 h-[0.625cqw] rounded-sm overflow-hidden"
              style={{
                background: 'rgba(0,0,0,0.35)',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.04)',
                border: `1px solid ${GOLD} 0.18)`,
              }}
            >
              <div
                className="h-full rounded-xs"
                style={{
                  width: `${pct}%`,
                  ...noiseBarStyle(`linear-gradient(180deg, ${color} 0%, ${color}aa 100%)`),
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18)',
                }}
              />
            </div>
            <span
              className="text-[0.6875cqw] tabular-nums"
              style={{ color: TEXT_PRIMARY, minWidth: '2.25cqw', textAlign: 'right' }}
            >
              {avg.toFixed(1)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Bar list of keyword frequencies. Same shape as before, factored out
 * so the popover body and any future inline use can share it. Top 5
 * by count keeps even a fully-keyworded deck readable.
 */
function KeywordBars({ metrics }) {
  const keywords = (metrics.keywords || [])
    .slice()
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
  if (keywords.length === 0) {
    return (
      <div className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>
        {t('No keyworded spells yet.')}
      </div>
    );
  }
  const max = Math.max(...keywords.map((k) => k.count), 1);
  return (
    <div className="flex flex-col gap-[0.3cqw]">
      {keywords.map((k) => {
        const pct = (k.count / max) * 100;
        return (
          <div key={k.key} className="flex items-center gap-2">
            <span
              className="text-[0.6875cqw] tabular-nums"
              style={{ color: TEXT_BODY, minWidth: '5.5cqw' }}
            >
              {KEYWORD_LABEL[k.key] || k.key}
            </span>
            <div
              className="flex-1 h-[0.55cqw] rounded-xs overflow-hidden"
              style={{
                background: 'rgba(0,0,0,0.4)',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.55)',
                border: `1px solid ${GOLD} 0.18)`,
              }}
            >
              <div
                className="h-full"
                style={{
                  width: `${pct}%`,
                  ...noiseBarStyle(
                    `linear-gradient(180deg, ${ACCENT_GOLD} 0%, ${ACCENT_GOLD}aa 100%)`,
                  ),
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18)',
                }}
              />
            </div>
            <span
              className="text-[0.75cqw] tabular-nums"
              style={{ color: TEXT_PRIMARY, minWidth: '1.5cqw', textAlign: 'right' }}
            >
              {k.count}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const POPOVER_OFFSET = 10;
const VIEWPORT_PAD = 8;
const HOVER_CLOSE_DELAY = 120;

/**
 * Hover-or-click popover for the keyword breakdown. Stays open while
 * the cursor is on either the trigger button or the floating panel,
 * which lets the user actually mouse into the popover to read it.
 * a pure `Tooltip` wouldn't allow that, and would also have to be
 * `pointer-events-none` to prevent re-trigger flicker.
 *
 * Click toggles for keyboard/touch users. Click outside closes.
 * Portalled to <body> so the panel escapes the .app-canvas
 * letterboxing and overflow-clipping inside the footer.
 */
class KeywordPopover extends Component {
  constructor(props) {
    super(props);
    this.state = { open: false, pos: null };
    this.triggerRef = null;
    this.panelRef = null;
    this.closeTimer = null;
  }

  componentWillUnmount() {
    clearTimeout(this.closeTimer);
    document.removeEventListener('mousedown', this.handleDocMouseDown);
  }

  scheduleClose = () => {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(), HOVER_CLOSE_DELAY);
  };

  cancelClose = () => {
    clearTimeout(this.closeTimer);
  };

  open = () => {
    this.cancelClose();
    if (this.state.open) return;
    this.setState({ open: true }, this.measure);
    document.addEventListener('mousedown', this.handleDocMouseDown);
  };

  close = () => {
    if (!this.state.open) return;
    this.setState({ open: false, pos: null });
    document.removeEventListener('mousedown', this.handleDocMouseDown);
  };

  handleDocMouseDown = (e) => {
    if (this.triggerRef?.contains(e.target)) return;
    if (this.panelRef?.contains(e.target)) return;
    this.close();
  };

  measure = () => {
    const trigger = this.triggerRef;
    const panel = this.panelRef;
    if (!trigger || !panel) return;
    const tRect = trigger.getBoundingClientRect();
    const pRect = panel.getBoundingClientRect();

    // Prefer above the trigger (footer sits at the bottom of the
    // screen, so opening upward is almost always the right call).
    let top = tRect.top - pRect.height - POPOVER_OFFSET;
    if (top < VIEWPORT_PAD) {
      top = tRect.bottom + POPOVER_OFFSET;
    }
    let left = tRect.left + tRect.width / 2 - pRect.width / 2;
    left = Math.max(
      VIEWPORT_PAD,
      Math.min(left, window.innerWidth - pRect.width - VIEWPORT_PAD),
    );
    this.setState({ pos: { left, top } });
  };

  render() {
    const { metrics, totalKeywords } = this.props;
    const { open, pos } = this.state;

    const panel = open
      ? createPortal(
          <div
            ref={(el) => { this.panelRef = el; }}
            onMouseEnter={this.cancelClose}
            onMouseLeave={this.scheduleClose}
            className="fixed z-[1300]"
            style={{
              left: pos?.left ?? -9999,
              top: pos?.top ?? -9999,
              visibility: pos ? 'visible' : 'hidden',
              width: '20cqw',
              // Fully opaque base. The popover floats above the canvas
              // and any alpha here would let the game UI bleed through.
              // `rgb(12,10,8)` keeps the same stone hue as PANEL_BG, just
              // without the 0.92 transparency. The shorthand also sets
              // background-color from the trailing color, so we don't
              // need a separate `backgroundColor` belt-and-braces.
              background: `${CONTENT_BG_DEFAULT}, rgb(12, 10, 8)`,
              backgroundBlendMode: 'overlay, normal',
              border: `1px solid ${GOLD} 0.5)`,
              borderRadius: 'var(--radius-lg)',
              boxShadow: [
                '0 18px 36px rgba(0,0,0,0.65)',
                `0 0 20px ${GOLD} 0.1)`,
                'inset 0 1px 0 rgba(255,255,255,0.05)',
                'inset 0 -1px 0 rgba(0,0,0,0.25)',
              ].join(', '),
              padding: '0.85cqw 1cqw',
            }}
          >
            <FourCorners color={`${GOLD} 0.55)`} />
            <div
              className="text-[0.625cqw] font-semibold mb-[0.5cqw]"
              style={{ color: TEXT_MUTED }}
            >
              {t('Keywords in your picks')}
            </div>
            <KeywordBars metrics={metrics} />
          </div>,
          document.body,
        )
      : null;

    return (
      <>
        <button
          ref={(el) => { this.triggerRef = el; }}
          type="button"
          onMouseEnter={this.open}
          onMouseLeave={this.scheduleClose}
          onFocus={this.open}
          onBlur={this.scheduleClose}
          onClick={(e) => {
            e.stopPropagation();
            if (this.state.open) this.close();
            else this.open();
          }}
          className="inline-flex items-center gap-1.5 text-[0.625cqw] font-semibold transition-all cursor-pointer"
          style={{
            color: open ? ACCENT_GOLD : TEXT_BODY,
            background: open ? `${GOLD} 0.18)` : `${GOLD} 0.08)`,
            border: `1px solid ${GOLD} ${open ? 0.55 : 0.3})`,
            borderRadius: 'var(--radius-md)',
            padding: '0.25cqw 0.6cqw',
            boxShadow: open
              ? `inset 0 1px 0 rgba(255,255,255,0.08), 0 0 8px ${GOLD} 0.18)`
              : 'inset 0 1px 0 rgba(255,255,255,0.05)',
          }}
        >
          <span>{t('Keywords')}</span>
          <span
            className="tabular-nums"
            style={{ color: open ? ACCENT_GOLD : TEXT_MUTED }}
          >
            {totalKeywords}
          </span>
          <svg
            width="0.6cqw"
            height="0.6cqw"
            viewBox="0 0 8 8"
            aria-hidden="true"
            style={{
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 120ms ease',
            }}
          >
            <path
              d="M1.5 5L4 2.5L6.5 5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {panel}
      </>
    );
  }
}

function PanelHeading({ eyebrow, value, hint, trailing }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-[0.6cqw]">
      <div>
        <div
          className="text-[0.625cqw] font-semibold"
          style={{ color: TEXT_MUTED }}
        >
          {eyebrow}
        </div>
        <div
          className="text-[1.1cqw] font-bold leading-tight arena-heading"
          style={{ color: ACCENT_GOLD, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
        >
          {value}
        </div>
      </div>
      {(trailing || hint) ? (
        <div className="flex items-center gap-2.5 shrink-0">
          {trailing}
          {hint ? (
            <div className="text-[0.6875cqw] tabular-nums" style={{ color: TEXT_BODY, opacity: 0.85 }}>
              {hint}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SectionLabel({ children, className = '' }) {
  return (
    <div
      className={`text-[0.5625cqw] font-semibold mb-[0.4cqw] ${className}`.trim()}
      style={{ color: TEXT_MUTED }}
    >
      {children}
    </div>
  );
}

export default class DraftFooter extends Component {
  render() {
    const { seenEntries = [], seenPackCount = 0, chosenCards = [] } = this.props;

    const circulationSpells = seenEntries.filter((e) => e.zone === 'spellbook');
    const picksMetrics = buildSorceryDeckMetrics(chosenCards);

    return (
      <div className="w-full px-6 pb-4 shrink-0">
        <div
          className="relative w-full rounded-xl overflow-hidden"
          style={{
            background: `${CONTENT_BG_DEFAULT}, ${PANEL_BG}`,
            backgroundBlendMode: 'overlay, normal',
            border: `1px solid ${GOLD} 0.55)`,
            boxShadow: [
              '0 18px 36px rgba(0,0,0,0.55)',
              '0 0 28px rgba(180,140,60,0.08)',
              'inset 0 1px 0 rgba(255,255,255,0.05)',
              'inset 0 -1px 0 rgba(0,0,0,0.25)',
              'inset 0 0 24px rgba(0,0,0,0.25)',
            ].join(', '),
          }}
        >
          <ChartNoiseDefs />
          <FourCorners color={`${GOLD} 0.6)`} />

          {/* Seam between the two halves. A faded gold gradient + a small
              embossed knurl in the middle reads as a forged divider
              instead of a flat line. */}
          <div
            className="absolute top-[0.75cqw] bottom-[0.75cqw] left-1/2 -translate-x-1/2 pointer-events-none"
            style={{
              width: '1px',
              background: `linear-gradient(180deg, transparent 0%, ${GOLD} 0.45) 18%, ${GOLD} 0.55) 50%, ${GOLD} 0.45) 82%, transparent 100%)`,
            }}
          />
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            style={{
              width: '0.4cqw',
              height: '0.4cqw',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${ACCENT_GOLD} 0%, ${GOLD} 0.6) 70%, transparent 100%)`,
              boxShadow: `0 0 0.5cqw ${GOLD} 0.45)`,
            }}
          />

          <div className="relative grid grid-cols-2">
            {/* LEFT. Cards in Circulation */}
            <div className="px-[1.25cqw] py-[0.9cqw] flex flex-col gap-[0.6cqw] min-w-0">
              <PanelHeading
                eyebrow={t('Draft Pool')}
                value={t('Cards in Circulation')}
                hint={
                  seenEntries.length > 0
                    ? (seenEntries.length === 1
                      ? (seenPackCount === 1 ? t('1 card · 1 pack') : t('1 card · {packs} packs', { packs: seenPackCount }))
                      : (seenPackCount === 1 ? t('{cards} cards · 1 pack', { cards: seenEntries.length }) : t('{cards} cards · {packs} packs', { cards: seenEntries.length, packs: seenPackCount })))
                    : t('Awaiting first pack')
                }
              />

              <div>
                <SectionLabel>{t('Element distribution')}</SectionLabel>
                <ElementBar entries={seenEntries} />
              </div>

              <div>
                <SectionLabel>{t('Average cost by element')}</SectionLabel>
                <AvgCostByElementBars entries={circulationSpells} />
              </div>
            </div>

            {/* RIGHT. Your Picks */}
            <div className="px-[1.25cqw] py-[0.9cqw] flex flex-col gap-[0.35cqw] min-w-0">
              <PanelHeading
                eyebrow={t('Your Picks')}
                value={t('Deck in Progress')}
                hint={
                  chosenCards.length > 0
                    ? (chosenCards.length === 1 ? t('1 pick') : t('{n} picks', { n: chosenCards.length }))
                    : t('Nothing picked yet')
                }
                trailing={
                  <KeywordPopover
                    metrics={picksMetrics}
                    totalKeywords={picksMetrics.keywords?.reduce((s, k) => s + k.count, 0) || 0}
                  />
                }
              />

              <div className="flex flex-col min-w-0">
                <SectionLabel>{t('Mana curve')}</SectionLabel>
                <ManaCurveChart
                  metrics={picksMetrics}
                  heightClass="h-[6.5cqw]"
                  emptyLabel={t('Pick spells to chart your curve.')}
                  showReferenceLine={false}
                  compact
                />
              </div>

              <div>
                <SectionLabel>{t('Element distribution')}</SectionLabel>
                <ElementBar entries={chosenCards} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
